<template>
  <section id="next-steps" ref="next-steps" :class="$style.section">
    <div class="layout-container">
      <h2 :class="$style.title">{{ t('myProperties.landing.nextSteps.title') }}</h2>

      <div :class="$style.cardsGrid">
        <div
          v-for="(card, index) in cards"
          :key="index"
          :class="[$style.card, { [$style.is24]: !isHg }]"
        >
          <div :class="$style.graphicWrapper">
            <div :class="{ [$style.graphicCircle]: !isHg }">
              <img
                :src="card.image"
                alt=""
                :class="[$style.graphicImage, { [$style.graphicImageHg]: isHg }]"
                loading="lazy"
              />
            </div>
          </div>

          <h3 :class="$style.cardTitle">{{ card.title }}</h3>

          <HgLink
            v-if="card.linkText && (card.linkUrl || card.openModal)"
            :href="card.linkUrl"
            :class="$style.cardLink"
            @click="onCardClick($event, card)"
          >
            {{ card.linkText }}
          </HgLink>
        </div>
      </div>

      <div :class="$style.infoGrid">
        <div
          v-for="(info, index) in infoItems"
          :key="index"
          :class="[$style.infoItem, { [$style.is24]: !isHg }]"
        >
          <img v-if="isHg" :src="info.svgIcon" alt="" :class="$style.infoIcon" />
          <div v-else :class="$style.iconWrapper">
            <HgIcon :icon="info.icon" size="medium" color="black" />
          </div>
          <p :class="$style.infoText">{{ info.text }}</p>
        </div>
      </div>
    </div>
  </section>
  <InformedContactModal
    ref="contactModalRef"
    :from="ContactModalFrom.NextStepsTeaser"
    :modalType="ContactModalType.CALL_ME_BACK"
  />
</template>

<script setup lang="ts">
import nextStepsBasedOnIcon from '@/assets/icons/next-steps-based-on.svg?url';
import nextStepsUsedByIcon from '@/assets/icons/next-steps-used-by.svg?url';
import { InformedContactModal } from '@/components/common';
import {
  getPlatformDomain,
  getTestPlatformDomain,
  isPlatformHg,
} from '@/helpers/Platform/Platform';
import { getPropertyLandingImageUrl } from '@/helpers/Url/Url';
import { OVERVIEW_PAGE_PATH_SOURCE_LANDING } from '@/router/routes/constants';
import { trackingClickEvent } from '@/tracking/tracking';
import { TRACKING_ACTION, TRACKING_LABEL } from '@/tracking/types';
import { ContactModalFrom, ContactModalType } from '@/types';
import { HgIcon, HgLink, Icon } from '@smg-real-estate/domus';
import { computed, onMounted, ref, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

interface Card {
  image: string;
  title: string;
  trackingAction: TRACKING_ACTION;
  linkText?: string;
  linkUrl?: string;
  useRouterPush?: boolean;
  openModal?: boolean;
}

const isHg = isPlatformHg();
const { locale, t } = useI18n();
const router = useRouter();
const nextStepsOneHg = getPropertyLandingImageUrl('next-steps/next_steps_one_hg_v2.png');
const nextStepsOneIs24 = getPropertyLandingImageUrl('next-steps/next_steps_one_is24_v2.png');
const nextStepsTwoHg = getPropertyLandingImageUrl('next-steps/next_steps_two_hg_v2.png');
const nextStepsTwoIs24 = getPropertyLandingImageUrl('next-steps/next_steps_two_is24.png');
const nextStepsThreeHg = getPropertyLandingImageUrl('next-steps/next_steps_three_hg_v2.png');
const nextStepsThreeIs24 = getPropertyLandingImageUrl('next-steps/next_steps_three_is24.png');

const platformBasePath =
  import.meta.env.VUE_APP_STAGE === 'prod' ? getPlatformDomain() : getTestPlatformDomain();

const submitAdPaths = {
  de: '/de/insertion/inserat-online-erfassen',
  en: '/en/insertion/submit-ad-online',
  fr: '/fr/insertion/publier-soi-meme-en-ligne',
  it: '/it/insertion/inserire-in-linea',
} as const;

const submitAdUrl = computed(() => `${platformBasePath}${submitAdPaths[locale.value]}`);
const myPropertiesLandingPath = computed(() => OVERVIEW_PAGE_PATH_SOURCE_LANDING[locale.value]);

const contactModalRef = ref<InstanceType<typeof InformedContactModal> | null>(null);

const cards = computed<Card[]>(() => [
  {
    image: isHg ? nextStepsOneHg : nextStepsOneIs24,
    title: t('myProperties.landing.nextSteps.cards.selling.title'),
    linkText: t('myProperties.landing.nextSteps.cards.selling.linkText'),
    openModal: true,
    trackingAction: TRACKING_ACTION.OPEN_SELLER_LEAD_MODAL,
  },
  {
    image: isHg ? nextStepsTwoHg : nextStepsTwoIs24,
    title: t('myProperties.landing.nextSteps.cards.decisions.title'),
    linkText: t('myProperties.landing.nextSteps.cards.decisions.linkText'),
    linkUrl: submitAdUrl.value,
    trackingAction: TRACKING_ACTION.OPEN_INSERT_AD,
  },
  {
    image: isHg ? nextStepsThreeHg : nextStepsThreeIs24,
    title: t('myProperties.landing.nextSteps.cards.clarity.title'),
    linkText: t('myProperties.landing.nextSteps.cards.clarity.linkText'),
    linkUrl: myPropertiesLandingPath.value,
    useRouterPush: true,
    trackingAction: TRACKING_ACTION.GO_TO_MY_REAL_ESTATE,
  },
]);

const onCardClick = (event: MouseEvent, card: Card) => {
  trackingClickEvent(card.trackingAction, TRACKING_LABEL.NEXT_STEPS_TEASER);

  if (card.openModal) {
    event.preventDefault();
    contactModalRef.value?.open();
  } else if (card.useRouterPush && card.linkUrl) {
    event.preventDefault();
    router.push(card.linkUrl);
  }
};

interface InfoItem {
  icon: Icon;
  svgIcon: string;
  text: string;
}

const infoItems = computed<InfoItem[]>(() => [
  {
    icon: Icon.HouseHome,
    svgIcon: nextStepsUsedByIcon,
    text: t('myProperties.landing.nextSteps.info.usedBy.opch'),
  },
  {
    icon: Icon.TablePreview,
    svgIcon: nextStepsBasedOnIcon,
    text: t('myProperties.landing.nextSteps.info.basedOn.opch'),
  },
]);

const componentRef = useTemplateRef('next-steps');
const route = useRoute();
onMounted(() => {
  if (route.hash === '#next-steps') {
    componentRef.value?.scrollIntoView({ behavior: 'smooth' });
  }
});
</script>

<style lang="scss" module>
@use '@/assets/variables' as *;

.section {
  margin-top: $space-400;
  margin-bottom: $space-200;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 $space-100;

  @include grid-media-query(sm) {
    padding: 0 $space-200;
  }

  @include grid-media-query(md) {
    padding: 0 $space-500;
  }

  @include grid-media-query(lg) {
    padding: 0 $space-600;
  }
}

.title {
  @include heading-2;
  color: $color-black;
  text-align: center;
  margin-bottom: $space-300;
}

.cardsGrid {
  display: grid;
  grid-template-columns: 1fr;
  gap: $space-100;
  width: 100%;
  margin-bottom: $space-100;

  @include grid-media-query(sm) {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  }
}

.card {
  background-color: $color-grey-30;
  border-radius: $border-radius-xl;
  padding: $space-200 $space-100;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  transition: transform 0.2s ease;

  &.is24 {
    background-color: $color-teal-50;
  }

  &:hover {
    transform: translateY(-4px);
  }
}

.graphicWrapper {
  margin-bottom: $space-300;
}

.graphicCircle {
  width: $space-1000;
  height: $space-1000;
  background-color: #eef4ff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.graphicImage {
  width: 100%;
  height: auto;
  object-fit: contain;
}

.graphicImageHg {
  width: calc($space-1000 * 0.9);
  height: auto;
}

.cardTitle {
  @include heading-4;
  color: $color-black;
  min-height: 2.5em;
  margin: 0;
  display: -webkit-box;
  line-clamp: 2;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-wrap: balance;
}

.cardLink {
  @include body-3;
  color: $color-blue-60;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}

.infoGrid {
  display: grid;
  grid-template-columns: 1fr;
  gap: $space-100;
  width: 100%;

  @include grid-media-query(sm) {
    grid-template-columns: repeat(2, 1fr);
  }
}

.infoItem {
  background-color: $color-grey-30;
  border-radius: $border-radius-l;
  padding: $space-200 $space-300;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: $space-200;

  &.is24 {
    background-color: $color-teal-50;

    .iconWrapper {
      background-color: $color-teal-100;
    }
  }

  @include grid-media-query(sm) {
    flex-direction: row;
  }
}

.iconWrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background-color: $color-green-80;
  border-radius: 50%;
  color: $color-white;
  flex-shrink: 0;
}

.infoIcon {
  width: $space-300;
  height: $space-300;
}

.infoText {
  @include body-3;
  color: $color-black;
  margin: 0;
}
</style>
